import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";

const ACTION_COLOR = {
  insert: "bg-green-100 text-green-800 border-green-200",
  update: "bg-blue-100 text-blue-800 border-blue-200",
  delete: "bg-red-100 text-red-800 border-red-200",
  login: "bg-slate-100 text-slate-700 border-slate-200",
  logout: "bg-slate-100 text-slate-700 border-slate-200",
  view: "bg-slate-100 text-slate-700 border-slate-200",
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/audit-log");
        setRows(data);
      } catch (e) {
        const detail =
          e?.response?.data?.detail ||
          e?.response?.statusText ||
          e?.message ||
          "Unknown error";
        setError(`${e?.response?.status || ""} ${detail}`.trim());
        toast.error(`Failed to load audit log: ${detail}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div data-testid="audit-page" className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-slate-400">Settings</div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>Audit Log</h1>
        <p className="text-sm text-slate-500 mt-1">
          Latest 200 actions across the system.
        </p>
      </div>

      {error && (
        <div data-testid="audit-error" className="bg-red-50 border border-red-200 rounded-md px-4 py-3 text-sm text-red-700">
          <div className="font-semibold mb-0.5">Failed to load audit log</div>
          <div className="font-mono text-xs text-red-600">{error}</div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm" data-testid="audit-table">
          <thead className="bg-slate-50 border-b">
            <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">When</th>
              <th className="px-4 py-3">User</th>
              <th className="px-4 py-3">Action</th>
              <th className="px-4 py-3">Table</th>
              <th className="px-4 py-3">Record</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">Loading...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No activity yet</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 text-xs text-slate-500 font-mono">{formatDateTime(r.timestamp)}</td>
                <td className="px-4 py-3 text-slate-700">{r.user_email || "system"}</td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className={`${ACTION_COLOR[r.action] || "bg-slate-100 text-slate-700"} border uppercase text-[10px]`}>
                    {r.action}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-xs font-mono text-slate-600">{r.table_name}</td>
                <td className="px-4 py-3 text-xs font-mono text-slate-500 truncate max-w-xs">{r.record_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
